import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Work,
  Assignment,
  Description,
  Person,
  Logout,
  Inventory2,
  RequestQuote as RequestQuoteIcon,
  Calculate as CalculateIcon,
  EnergySavingsLeaf,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext.tsx';

const DRAWER_WIDTH = 240;
const SIDEBAR_BG   = '#0f172a';
const NAV_TEXT     = '#94a3b8';
const NAV_SELECTED = '#ffffff';
const NAV_ICON_ACTIVE = '#10b981';

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout, isManager, isSurveyor, isCustomer } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl]     = useState<null | HTMLElement>(null);

  const handleDrawerToggle   = () => setMobileOpen((o) => !o);
  const handleMenuOpen       = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose      = () => setAnchorEl(null);
  const handleLogout         = () => { handleMenuClose(); logout(); navigate('/login'); };
  const handleProfileClick   = () => { handleMenuClose(); navigate('/profile'); };

  const getNavItems = () => {
    const base = [{ text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }];

    if (isCustomer) return [
      ...base,
      { text: 'My Quotes',  icon: <RequestQuoteIcon />, path: '/my-quotes' },
      { text: 'My Projects', icon: <Work />,            path: '/projects' },
      { text: 'Surveys',    icon: <Assignment />,       path: '/surveys' },
      { text: 'Documents',  icon: <Description />,      path: '/documents' },
    ];

    if (isSurveyor) return [
      ...base,
      { text: 'Customers', icon: <People />,     path: '/customers' },
      { text: 'Projects',  icon: <Work />,       path: '/projects' },
      { text: 'Surveys',   icon: <Assignment />, path: '/surveys' },
      { text: 'Documents', icon: <Description />, path: '/documents' },
    ];

    return [
      ...base,
      { text: 'Customers',        icon: <People />,          path: '/customers' },
      { text: 'Projects',         icon: <Work />,            path: '/projects' },
      { text: 'Quote Requests',   icon: <RequestQuoteIcon />, path: '/quote-requests' },
      { text: 'Quote Calculator', icon: <CalculateIcon />,   path: '/quotes/calculator' },
      { text: 'Surveys',          icon: <Assignment />,      path: '/surveys' },
      { text: 'Documents',        icon: <Description />,     path: '/documents' },
      { text: 'Inventory',        icon: <Inventory2 />,      path: '/inventory' },
    ];
  };

  const navItems = getNavItems();

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR_BG }}>
      {/* Brand */}
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 1.5,
          bgcolor: '#10b981',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <EnergySavingsLeaf sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{
            color: '#f8fafc', fontWeight: 700, fontSize: '1rem',
            lineHeight: 1.2, letterSpacing: '-0.02em',
          }}>
            Zena
          </Typography>
          <Typography sx={{
            color: '#475569', fontSize: '0.625rem', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            CRM
          </Typography>
        </Box>
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, px: 1.5, overflowY: 'auto', py: 1 }}>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {navItems.map((item) => {
            const selected =
              location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  sx={{
                    borderRadius: 1.5,
                    py: 0.875,
                    px: 1.5,
                    color: selected ? NAV_SELECTED : NAV_TEXT,
                    bgcolor: selected ? 'rgba(255,255,255,0.08)' : 'transparent',
                    transition: 'background 0.15s, color 0.15s',
                    '&:hover': {
                      bgcolor: selected ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
                      color: selected ? NAV_SELECTED : '#cbd5e1',
                    },
                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: 36,
                    color: selected ? NAV_ICON_ACTIVE : 'inherit',
                    '& svg': { fontSize: 18 },
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: selected ? 600 : 400,
                      letterSpacing: '-0.01em',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* User footer */}
      <Box sx={{ px: 2, py: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{
            width: 32, height: 32, flexShrink: 0,
            bgcolor: '#10b981', fontSize: '0.8125rem', fontWeight: 700,
          }}>
            {user?.first_name?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              color: '#f1f5f9', fontSize: '0.8125rem', fontWeight: 600,
              lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user?.full_name}
            </Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.6875rem', textTransform: 'capitalize' }}>
              {user?.role}
            </Typography>
          </Box>
          <Tooltip title="Sign out" placement="top">
            <IconButton
              size="small"
              onClick={handleLogout}
              sx={{ color: '#475569', '&:hover': { color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.06)' } }}
            >
              <Logout sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );

  const drawerSx = {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      bgcolor: SIDEBAR_BG,
      borderRight: 'none',
    },
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar — only meaningful on mobile; on desktop it's a thin white header */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 56 }, px: { xs: 2, md: 3 } }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, display: { md: 'none' }, color: 'text.secondary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile brand */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, flex: 1 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: 1,
              bgcolor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EnergySavingsLeaf sx={{ color: '#fff', fontSize: 14 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
              Zena
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

          {/* Right — user menu (always visible for quick access; desktop also has sidebar footer) */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' }, fontSize: '0.8125rem' }}
            >
              {user?.full_name}
            </Typography>
            <IconButton onClick={handleMenuOpen} size="small">
              <Avatar sx={{ width: 30, height: 30, bgcolor: '#10b981', fontSize: '0.8125rem', fontWeight: 700 }}>
                {user?.first_name?.charAt(0)}
              </Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Profile dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 160, borderRadius: 2, border: '1px solid #e7e5e4', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileClick} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
          Profile
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout} sx={{ fontSize: '0.875rem', gap: 1.5, color: 'error.main' }}>
          <Logout sx={{ fontSize: 16 }} />
          Sign out
        </MenuItem>
      </Menu>

      {/* Sidebar — mobile */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, ...drawerSx }}
        >
          {sidebarContent}
        </Drawer>

        {/* Sidebar — desktop */}
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, ...drawerSx }}
          open
        >
          {sidebarContent}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          bgcolor: 'background.default',
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '56px',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
